// @flow
import React, { Component } from 'react';
import AccountSwitcherListItem from 'app/common/react/components/growth/unauth/signup/AccountSwitcher/AccountSwitcherListItem';
import AccountSwitcherLoginModal from 'app/common/react/components/AccountSwitcherLoginModal/AccountSwitcherLoginModal';
import BusinessAccountList from 'app/common/react/components/growth/unauth/signup/AccountSwitcher/BusinessAccountList';
import CONTAINER_NAME from 'app/common/react/components/growth/unauth/lib/loggingContainerConstants';
import ContextLogger from 'app/common/lib/ContextLogger';
import i18n from 'app/common/lib/i18n';
import LoadingMask from 'app/common/react/components/growth/unauth/signup/LoadingMask/LoadingMask';
import login from 'app/common/react/components/growth/unauth/signup/UserLogin';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import templateConst from 'app/common/lib/templateConst';
import toCamelCase from 'app/common/lib/util/toCamelCase';
import type { AuthViewer } from 'app/packages/pinterest-flow-types/viewer';
import type { BusinessUser } from 'app/common/lib/BusinessOwnershipUtils';
import { compose } from 'redux';
import { deepMapStringKeys } from 'app/common/lib/util/ObjectUtils';
import { Experiment } from 'app/common/react/components/experiments';
import { MultiTreatmentExp } from 'app/common//lib/experiment';
import { startTiming } from 'app/common/lib/TraceLogger/logTraceEvent';
import { track } from 'app/common/lib/react/analytics';
import type {
  BusinessAccountCreds,
  SwitchAccountCreds,
} from 'app/packages/pinterest-flow-types/authentication';
import withResource, { type Resource } from 'app/common/lib/react/withResource';
import {
  getStoredUserAccounts,
  handleAccountSwitchFail,
  handleAccountSwitchSuccess,
  MAX_ACCOUNTS,
  removeUserLoggedIn,
  trackMultipleAccounts,
  type MultipleAccountsUser,
} from 'app/common/lib/MultipleAccountsUtils';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { Avatar, Box, Button, Divider, Text, Touchable } from 'gestalt';

type State = {|
  failedAccount: ?MultipleAccountsUser,
  showLoginModal: boolean,
  isLoading: boolean,
|};

type OwnProps = {|
  container: string,
  user: Resource<any>,
  showLogin?: Function,
  viewer: AuthViewer,
  onNavigateButtonClick?: () => void,
  enterProMode?: () => void,
|};

type HOCProps = {| requestContext: RequestContextType, businessUser: Resource<BusinessUser> |};

type Props = {| ...OwnProps, ...HOCProps |};

export class AccountSwitcher extends Component<Props, State> {
  state: State = {
    failedAccount: null,
    showLoginModal: false,
    isLoading: false,
  };

  componentDidMount() {
    if (this.props.container === CONTAINER_NAME.ACCOUNT_SWITCHER_MODAL) {
      trackMultipleAccounts('modal.shown');
    }
  }

  componentWillUnmount() {
    if (this.props.container === CONTAINER_NAME.ACCOUNT_SWITCHER_MODAL) {
      trackMultipleAccounts('modal.dismiss');
    }
  }

  handleAddAccount = () => {
    trackMultipleAccounts('add_account_click', null, this.props.container);
    if (this.props.container === CONTAINER_NAME.ACCOUNT_SWITCHER_MODAL && this.props.showLogin) {
      this.props.showLogin();
    } else {
      this.setState({ showLoginModal: true });
    }
  };

  trackProfileNavigateClick = (location: string) => {
    startTiming(PCONST.thrift.TracingClientTypes.PWTAction.OWN_PROFILE);
    trackMultipleAccounts(`current_profile_${location}_click`, null, this.props.container);
  };

  handleSwitch = (account: MultipleAccountsUser) => {
    const { container, requestContext } = this.props;
    const isStandalone = account.partner && account.partner.is_create;

    this.setState({ isLoading: true });

    track(PCONST.thrift.EventType.CLICK, {
      component: PCONST.thrift.ComponentType.ACCOUNT_SWITCHER,
      element: PCONST.thrift.ElementType.SWITCH_TO_STORED_MULTI_ACCOUNT_BUTTON,
    });

    trackMultipleAccounts(
      'switch_account_click',
      {
        destination_user_id: account.id,
      },
      container,
    );
    // Login user with creds=switch_account
    const loginCreds: SwitchAccountCreds = {
      get_user: true,
      switch_account: account.id,
    };

    // Pause and flush impressions before switching accounts, so that
    // the impressions get logged under the correct user. See BUG-96331.
    ContextLogger.getInstance().pauseImpressionsAndFlush();

    login(loginCreds, { container: 'account_switcher' }, null, true).then(
      (loginResponse = {}) => {
        if (
          loginResponse.resource_response &&
          loginResponse.resource_response.data &&
          loginResponse.resource_response.data.success
        ) {
          const proModeHeaderExp = new MultiTreatmentExp(requestContext, 'web_pro_mode_header');
          const isEnabledFullWidth =
            proModeHeaderExp.check('enabled_full_width') ||
            proModeHeaderExp.check('enabled_full_width_employees');
          let url = isStandalone ? '/' : `/${account.username}`;

          if (isEnabledFullWidth) {
            url = `${templateConst.settings.CANONICAL_MAIN_URL}/${account.username}/`;
            if (isStandalone) {
              url = templateConst.settings.CANONICAL_MAIN_URL;
            }
          }

          handleAccountSwitchSuccess(url, account.id, container);
        } else {
          // API returned response, but auth failed
          handleAccountSwitchFail(account.id, container);
          // Handle deleting/reauthing user
          this.setState({ failedAccount: account, isLoading: false });
        }
      },
      (error) => {
        // Handle account switch error
        handleAccountSwitchFail(account.id, container);
        this.setState({ isLoading: false });
        throw error;
      },
    );
  };

  handleLinkedAccountSwitch = (account: MultipleAccountsUser) => {
    const { container, viewer } = this.props;

    // instead of switching to a stored user with the `switch_account` option
    // linked business account users need to use the `business_id` option, or logout
    // to go back to the owner pinner account
    this.setState({ isLoading: true });

    track(PCONST.thrift.EventType.CLICK, {
      component: PCONST.thrift.ComponentType.ACCOUNT_SWITCHER,
      element:
        account.partner && account.partner.isLinkedBusiness
          ? PCONST.thrift.ElementType.SWITCH_TO_LINKED_BUSINESS_BUTTON
          : PCONST.thrift.ElementType.SWITCH_TO_LINKED_OWNER_BUTTON,
    });

    // Pause and flush impressions before switching accounts, so that
    // the impressions get logged under the correct user. See BUG-96331.
    ContextLogger.getInstance().pauseImpressionsAndFlush();

    if (account.partner && account.partner.isLinkedBusiness) {
      const loginCreds: BusinessAccountCreds = {
        business_id: account.id,
        get_user: true,
      };

      login(loginCreds, { container: 'account_switcher' }, null, true).then(
        (loginResponse = {}) => {
          // After we have logged in the user successfully, redirect to desired page
          handleAccountSwitchSuccess('/', account.id, container);
        },
        (error) => {
          // Handle account switch error
          handleAccountSwitchFail(account.id, container);
          this.setState({ isLoading: false });
          throw error;
        },
      );
    } else {
      // log out of linked business account, fall back to owner pinner account
      ResourceFactory.create('UserSessionResource', {
        disable_auth_failure_redirect: true,
      })
        .callDelete()
        .then(
          () => {
            removeUserLoggedIn(viewer.id);
            handleAccountSwitchSuccess(`/${account.username}`, viewer.id, container);
          },
          (error) => {
            // Handle account switch error
            handleAccountSwitchFail(viewer.id, container);

            this.setState({ isLoading: false });
            throw error;
          },
        );
    }
  };

  handleCloseModal = () => this.setState({ showLoginModal: false });

  handleReauthAccount = () => {
    trackMultipleAccounts('switch_failure_prompt.login_click', null, this.props.container);
    this.setState({ failedAccount: null, showLoginModal: true });
  };

  handleRemoveUnauthAccount = (userId: string) => {
    trackMultipleAccounts(
      'switch_failure_prompt.account_removed',
      { removed_user_id: userId },
      this.props.container,
    );
    removeUserLoggedIn(userId);
    this.setState({ failedAccount: null });
  };

  getBusinessAccounts = () => {
    const {
      businessUser,
      viewer: { partner },
    } = this.props;

    if (!businessUser.data) {
      return null;
    }

    const businessAccounts =
      partner && partner.is_linked_business
        ? businessUser.data.owners
        : businessUser.data.businesses;

    return businessAccounts.reduce((memo, account: BusinessUser) => {
      memo[account.id] = deepMapStringKeys(toCamelCase, true)(account);

      if (account.partner && account.partner.is_linked_business) {
        memo[account.id].email = account.partner.biz_ownership_email;
      }

      return memo;
    }, {});
  };

  renderLoadingIndicator() {
    if (!this.state.isLoading) {
      return null;
    }

    return <LoadingMask />;
  }

  // $FlowFixMe Flow v0.104.0 upgrade supression.
  renderAccounts = (accounts: { [key: string]: MultipleAccountsUser }): Array<*> =>
    Object.keys(accounts).map((item: string, index: number) => (
      <li key={index}>
        <Touchable
          onTouch={() => {
            this.handleSwitch(accounts[item]);
          }}
        >
          <div className="AccountSwitcherListItem" style={{ height: 'auto' }}>
            <AccountSwitcherListItem
              large
              account={accounts[item]}
              viewerId={this.props.viewer.id}
            />
          </div>
        </Touchable>
        {index !== MAX_ACCOUNTS - 2 && <Divider />}
      </li>
    ));

  renderPinnerButtons = (username: string) => {
    const { onNavigateButtonClick } = this.props;

    return (
      <NavigateLink className="dangerouslyDisableFocusStyle" focusOnBody to={`/${username}/`}>
        <Button
          text={i18n._('View profile', 'Visit a user profile from account switcher')}
          accessibilityLabel={i18n._(
            'Go to current profile',
            'Visit a current user profile from account switcher',
          )}
          size="sm"
          color="red"
          onClick={() => {
            this.trackProfileNavigateClick('button');

            if (onNavigateButtonClick) {
              onNavigateButtonClick();
            }
          }}
        />
      </NavigateLink>
    );
  };

  renderBusinessButtons = (username: string) => {
    const {
      onNavigateButtonClick,
      enterProMode,
      viewer: { partner },
    } = this.props;

    return (
      <Box display="flex">
        <Box marginEnd={2}>
          <Experiment activate={false} name="web_pro_mode_header">
            {({ group: proModeHeaderGroup }) => {
              const inFullWidthExp = proModeHeaderGroup.startsWith('enabled_full_width');
              return (
                <NavigateLink
                  to={inFullWidthExp && partner && !partner.is_convert ? '/' : '/business/hub/'}
                  onClick={() => {
                    track(PCONST.thrift.EventType.CLICK, {
                      element: PCONST.thrift.ElementType.BUSINESS_HUB_BUTTON,
                    });

                    if (enterProMode) {
                      enterProMode();
                    }

                    if (onNavigateButtonClick) {
                      onNavigateButtonClick();
                    }
                  }}
                >
                  <Button
                    key="biz-hub-button"
                    text={i18n._('Business hub', 'Link to Business hub from account switcher')}
                    accessibilityLabel={i18n._(
                      'Go to Business hub',
                      'Visit the Business hub for the current business user',
                    )}
                    size="sm"
                    color="red"
                  />
                </NavigateLink>
              );
            }}
          </Experiment>
        </Box>
        <Experiment activate={false} name="web_pro_mode_header">
          {({ group: proModeHeaderGroup }) => {
            const inFullWidthExp = proModeHeaderGroup.startsWith('enabled_full_width');
            return (
              <NavigateLink
                className="dangerouslyDisableFocusStyle"
                focusOnBody
                to={
                  inFullWidthExp
                    ? `${templateConst.settings.CANONICAL_MAIN_URL}/${username}/`
                    : `/${username}/`
                }
              >
                <Button
                  text={i18n._('Profile', 'Visit a user profile from account switcher')}
                  accessibilityLabel={i18n._(
                    'Go to current user profile',
                    'Visit a current user profile from account switcher',
                  )}
                  size="sm"
                  onClick={() => {
                    this.trackProfileNavigateClick('button');

                    if (onNavigateButtonClick) {
                      onNavigateButtonClick();
                    }
                  }}
                />
              </NavigateLink>
            );
          }}
        </Experiment>
      </Box>
    );
  };

  render() {
    const {
      viewer: { partner },
    } = this.props;
    const { failedAccount, isLoading, showLoginModal } = this.state;
    const storedAccounts = getStoredUserAccounts();
    const businessAccounts = this.getBusinessAccounts();
    const num_accounts = Object.keys(storedAccounts).length;
    const currentUser = storedAccounts[this.props.viewer.id];
    // Remove current user from accounts
    delete storedAccounts[currentUser.id];
    const hasCustomImage =
      currentUser.imageLargeUrl &&
      !currentUser.imageLargeUrl.includes('static/images/user/default');

    return (
      <Box flex="grow">
        {showLoginModal && (
          <AccountSwitcherLoginModal
            container={this.props.container}
            onClose={this.handleCloseModal}
            email={failedAccount ? failedAccount.email : ''}
          />
        )}
        {failedAccount ? (
          <Box paddingX={4} paddingY={4} flex="grow">
            <Box marginBottom={3}>
              <Text>
                {i18n._(
                  "Oh no! You're no longer logged into",
                  'Full sentence: "Oh no! You\'re no longer logged into <user\'s account>". Text telling user that they are no longer logged into an account after trying to switch to it',
                )}
              </Text>
            </Box>
            <AccountSwitcherListItem viewerId={currentUser.id} account={failedAccount} />
            <Box marginTop={4}>
              <Button
                text={i18n._(
                  'Log in again',
                  'Text on button allowing user to log into an account after failing to switch to that account',
                )}
                color="red"
                onClick={this.handleReauthAccount}
              />
            </Box>
            <Box marginTop={3}>
              <Button
                text={i18n._(
                  'Remove account',
                  'Text on button allowing user to remove an account from their account switcher after failing to switch to it',
                )}
                onClick={() => this.handleRemoveUnauthAccount(failedAccount.id)}
              />
            </Box>
          </Box>
        ) : (
          <Box>
            {isLoading && this.renderLoadingIndicator()}
            <Box>
              {/* We always place the current user first in a special row */}
              <Box
                alignItems="center"
                direction="row"
                display="flex"
                marginTop={1}
                padding={4}
                position="relative"
                dangerouslySetInlineStyle={{ __style: { zIndex: 1 } }}
              >
                <Box marginRight={4}>
                  <NavigateLink
                    className="dangerouslyDisableFocusStyle"
                    focusOnBody
                    to={`/${currentUser.username}/`}
                  >
                    <Touchable onTouch={() => this.trackProfileNavigateClick('avatar')}>
                      <Avatar
                        name={currentUser.fullName || currentUser.username}
                        size="lg"
                        src={hasCustomImage ? currentUser.imageLargeUrl : ''}
                      />
                    </Touchable>
                  </NavigateLink>
                </Box>
                <Box flex="grow">
                  <Box marginBottom={2}>
                    <Text color="darkGray" size="md" truncate weight="bold">
                      {currentUser.fullName || currentUser.username}
                    </Text>
                    {currentUser.email && (
                      <Text color="darkGray" size="md" truncate>
                        {currentUser.email}
                      </Text>
                    )}
                  </Box>
                  {!!partner
                    ? this.renderBusinessButtons(currentUser.username)
                    : this.renderPinnerButtons(currentUser.username)}
                </Box>
              </Box>
              {businessAccounts ? (
                <BusinessAccountList
                  accounts={businessAccounts}
                  onClick={this.handleLinkedAccountSwitch}
                />
              ) : null}
              <Divider />
              {!(partner && partner.is_linked_business) ? (
                <Box>
                  <ul>{this.renderAccounts(storedAccounts)}</ul>
                </Box>
              ) : null}
            </Box>
            {num_accounts < MAX_ACCOUNTS &&
              !(partner && partner.is_linked_business) && (
                <Box paddingX={4} paddingY={3}>
                  <Button
                    text={i18n._(
                      'Add account',
                      'Short text on the button that allows a user to log in to another Pinterest account',
                    )}
                    onClick={() => this.handleAddAccount()}
                  />
                </Box>
              )}
          </Box>
        )}
      </Box>
    );
  }
}

export default compose(
  withRequestContext,
  withResource({
    key: 'businessUser',
    name: 'UserResource',
    options: ({ viewer }) => ({
      field_set_key: 'linked_partner',
      user_id: viewer.id,
    }),
  }),
)(AccountSwitcher);
